import { US_PREDICTIONS_READ } from "@/lib/predictionColumns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  buildSportFixtureSlug,
  parseSportFixtureSlug,
  type SportFixtureRow,
} from "@/lib/sportFixtures";

/**
 * Load every modelled market for one non-football match.
 *
 * The slug (`home-vs-away-YYYY-MM-DD`) is resolved by narrowing on sport + the
 * UTC calendar day server-side, then matching the team slugs client-side so
 * punctuation/casing differences in provider team names never 404 a page.
 */
export function useSportFixture(sportSlug: string | undefined, slug: string | undefined) {
  const parsed = slug ? parseSportFixtureSlug(slug) : null;

  return useQuery({
    queryKey: ["sport-fixture", sportSlug, slug],
    enabled: !!sportSlug && !!parsed,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SportFixtureRow[]> => {
      const from = `${parsed!.date}T00:00:00.000Z`;
      const to = `${parsed!.date}T23:59:59.999Z`;
      const { data, error } = await supabase
        .from(US_PREDICTIONS_READ)
        .select(
          "id, sport, home_team, away_team, commence_time, market_type, predicted_outcome, model_probability, implied_probability, edge, confidence_score, odds, point, risk_level, explanation, actual_outcome, was_correct, created_at",
        )
        .eq("sport", sportSlug!)
        .gte("commence_time", from)
        .lte("commence_time", to)
        .order("confidence_score", { ascending: false });
      if (error) throw error;

      const rows = ((data ?? []) as unknown) as SportFixtureRow[];
      return rows.filter(
        (r) =>
          buildSportFixtureSlug(r.home_team, r.away_team, r.commence_time) === slug,
      );
    },
  });
}

/**
 * Sibling matches in the same sport (same day forward), used for the
 * "other matches" internal-link block so no match page is an orphan.
 */
export function useSportFixtureSiblings(
  sportSlug: string | undefined,
  excludeSlug: string | undefined,
) {
  return useQuery({
    queryKey: ["sport-fixture-siblings", sportSlug],
    enabled: !!sportSlug,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SportFixtureRow[]> => {
      const { data, error } = await supabase
        .from(US_PREDICTIONS_READ)
        .select(
          "id, sport, home_team, away_team, commence_time, market_type, predicted_outcome, odds, confidence_score",
        )
        .eq("sport", sportSlug!)
        .order("commence_time", { ascending: false })
        .limit(120);
      if (error) throw error;

      const rows = ((data ?? []) as unknown) as SportFixtureRow[];
      const seen = new Set<string>();
      const out: SportFixtureRow[] = [];
      for (const r of rows) {
        if (!r.home_team || !r.away_team) continue;
        const s = buildSportFixtureSlug(r.home_team, r.away_team, r.commence_time);
        if (s === excludeSlug || seen.has(s)) continue;
        seen.add(s);
        out.push(r);
        if (out.length >= 12) break;
      }
      return out;
    },
  });
}
